import client from 'braintree-web/client';
import dataCollector from 'braintree-web/data-collector';
import paypal from 'braintree-web/paypal';
import threeDSecure from 'braintree-web/three-d-secure';
import find from 'lodash/find';
import get from 'lodash/get';
import cookies from 'react-cookies';
import { PAYMENT_METHODS, PAYMENT_TYPES } from '../config/constants/payment';
import COOKIES from '../config/cookies/cookies';
import { BRAINTREE } from '../config/setup/setup';
import {
  ERROR_MESSAGE,
  LIABILITY_ERROR,
  NO_EXPRESS_BUY,
  TRY_DIFFERENT,
  GENERIC_ERROR,
  PROGRESS_MESSAGE_WAIT,
  PROGRESS_MESSAGE_PAY,
  PROGRESS_MESSAGE_SUCCESS,
  PROGRESS_MESSAGE_FAILED,
} from '../config/text/text';
import { getAddonTotal } from './addonService';
import { trackPurchase } from './analytics';
import BraintreeService from './braintreeService';
import { isIESite, isWowcherBrand } from './pages';
import { getDefaultGateway } from './payment';

let currencyCode = 'GBP';
if (isIESite()) currencyCode = 'EUR';
const LOG_ERRORS = false;

const addonExpressBuyService = {
  addonType: '',
  cardDefault: null,
  currency: currencyCode,
  deviceFingerprintId: '',
  failedPayment: false,
  failure: null,
  instrumentToUse: null,
  isBraintree: false,
  paymentInfo: null,
  quantity: 0,
  setProgressMessage: null,
  success: null,
  threeDSecureInstance: null,
  threeDSRequired: true,
  tokenizationPayload: {},
  useBraintree: false,
};

const handleError = (message) => {
  addonExpressBuyService.setProgressMessage(message || TRY_DIFFERENT);
  addonExpressBuyService.failure();
};

const setupPayPal = async (clientError, clientInstance) => {
  if (
    addonExpressBuyService?.instrumentToUse?.type === PAYMENT_TYPES.card &&
    addonExpressBuyService.threeDSRequired
  ) {
    if (addonExpressBuyService.threeDSecureInstance) {
      addonExpressBuyService.threeDSecureInstance.teardown();
    }
    try {
      const threeDSecureInstance = await threeDSecure.create({
        client: clientInstance,
        version: 2,
      });
      if (threeDSecureInstance) {
        addonExpressBuyService.threeDSecureInstance = threeDSecureInstance;
      }
    } catch {
      handleError(TRY_DIFFERENT);
    }
  }
  try {
    const dataCollectorInstance = await dataCollector.create({
      client: clientInstance,
      paypal: true,
    });
    if (dataCollectorInstance) {
      // save device data for use in payment
      addonExpressBuyService.dataCollectorInstance = dataCollectorInstance;
      // continue with payment
      await addonExpressBuyService.continueBraintreePayment();
    }
  } catch {
    handleError(ERROR_MESSAGE);
  }

  try {
    const paypalInstance = await paypal.create({
      client: clientInstance,
    });
    if (paypalInstance) {
      addonExpressBuyService.paypalInstance = paypalInstance;
    }
  } catch {
    handleError(ERROR_MESSAGE);
  }
};

const createClient = (tokenResponse) => {
  addonExpressBuyService.clientId = tokenResponse.data.clientId;
  addonExpressBuyService.clientToken = tokenResponse.data.clientToken;
  client.create(
    { authorization: addonExpressBuyService.clientToken },
    setupPayPal,
  );
};

addonExpressBuyService.setupBraintree = async () => {
  const expressAuthToken = localStorage.getItem(COOKIES.expressAuthToken);

  try {
    let response;
    if (expressAuthToken) {
      response = await BraintreeService.getClientTokenWithExpressBuyToken(
        expressAuthToken,
      );
    } else {
      response = await BraintreeService.getClientToken();
    }
    if (response) {
      createClient(response);
    }
  } catch {
    handleError(ERROR_MESSAGE);
  }
};

const fetchPaymentOptions = async () => {
  addonExpressBuyService.setProgressMessage(PROGRESS_MESSAGE_WAIT);
  try {
    if (addonExpressBuyService.paymentInfo.paymentInstruments) {
      const gatewayValue = getDefaultGateway(
        addonExpressBuyService.paymentInfo.paymentInstruments,
      );
      addonExpressBuyService.useBraintree = gatewayValue === BRAINTREE;

      // If not express buyable
      if (!addonExpressBuyService.paymentInfo.buyNow) {
        handleError(NO_EXPRESS_BUY);

        return;
      }

      addonExpressBuyService.initiatePayment(
        addonExpressBuyService.makePayment,
      );
    } else {
      handleError(TRY_DIFFERENT);
    }
  } catch (error) {
    if (LOG_ERRORS) console.error(error);
    handleError(TRY_DIFFERENT);
  }
};

const getDefaultCard = async () => {
  try {
    const vaultedPayment = await BraintreeService.getVaultedCards();
    if (vaultedPayment && vaultedPayment[0] && vaultedPayment[0].token) {
      return vaultedPayment[0].token;
    }

    return null;
  } catch (error) {
    if (LOG_ERRORS) console.error(error);
    handleError(GENERIC_ERROR);

    return null;
  }
};

const finalStepPayment = async ({ responseNonce, makePayment }) => {
  if (BraintreeService.paymentMethod === PAYMENT_METHODS.paypal) {
    makePayment(BraintreeService.makePayment);
  } else {
    const payload = await addonExpressBuyService.threeDSecureInstance.verifyCard(
      {
        amount: getAddonTotal(addonExpressBuyService.addons)
          .toFixed(2)
          .toString(),
        bin: responseNonce.data.target.details.bin,
        merchantName: isWowcherBrand() ? 'Wowcher' : 'LivingSocial',
        nonce: responseNonce.data.target.nonce,
        onLookupComplete: (data, next) => {
          next();
        },
      },
    );
    if (payload) {
      if (payload.liabilityShiftPossible && !payload.liabilityShifted) {
        // Liablity possible, but failed challenge
        handleError(LIABILITY_ERROR);
      } else {
        // Either the liability shift is not possible, or the liability shifted
        if (payload.nonce) BraintreeService.nonce = payload.nonce;
        makePayment(BraintreeService.makePayment);
      }
    }
  }
};

const defineCard = async ({ makePayment }) => {
  const urlParameters = new URLSearchParams(window.location.search);
  const forceFail = urlParameters.get('forceFail') === 'true';
  if (forceFail) {
    addonExpressBuyService.failedPayment = true;
    handleError(NO_EXPRESS_BUY);

    return;
  }
  if (
    addonExpressBuyService.instrumentToUse.type === 'credit_card' &&
    addonExpressBuyService.threeDSRequired
  ) {
    try {
      const response = await getDefaultCard();
      if (response) {
        const responseNonce = await BraintreeService.getNonce(response);
        if (responseNonce && !responseNonce.data.target.nonce) {
          handleError(LIABILITY_ERROR);

          return;
        }
        finalStepPayment({ makePayment, responseNonce });
      }
    } catch (error) {
      if (LOG_ERRORS) console.error(error);
      addonExpressBuyService.failedPayment = true;
      handleError(NO_EXPRESS_BUY);
    }
  } else {
    makePayment(BraintreeService.makePayment);
  }
};

const expressCheckoutBraintree = ({ total, makePayment }) => {
  addonExpressBuyService.isBraintree = true;
  // setup Braintree
  addonExpressBuyService.setupBraintree();
  // the callback after setting up Braintree
  addonExpressBuyService.continueBraintreePayment = async () => {
    BraintreeService.nonce = null;
    BraintreeService.addons = addonExpressBuyService.addons;
    BraintreeService.isPostCheckout = true;
    BraintreeService.sendEmail = addonExpressBuyService.sendEmail;
    BraintreeService.totalAmountToPay = total;
    BraintreeService.productOrderId = addonExpressBuyService.productOrderId;
    BraintreeService.currency = addonExpressBuyService.currency;
    BraintreeService.deviceData = addonExpressBuyService.deviceData;
    BraintreeService.expressBuy = true;
    BraintreeService.id = addonExpressBuyService.dealId;
    if (addonExpressBuyService.instrumentToUse.type === PAYMENT_TYPES.card) {
      BraintreeService.paymentMethod = PAYMENT_METHODS.card;
    }
    if (addonExpressBuyService.instrumentToUse.type === PAYMENT_TYPES.paypal) {
      BraintreeService.paymentMethod = PAYMENT_METHODS.paypal;
    }
    defineCard({ makePayment });
  };
};

const definePaymentProcessor = ({ total, makePayment }) => {
  if (
    (addonExpressBuyService.instrumentToUse &&
      addonExpressBuyService.instrumentToUse.type === PAYMENT_TYPES.card &&
      addonExpressBuyService.useBraintree) ||
    addonExpressBuyService.instrumentToUse.type === PAYMENT_TYPES.paypal
  ) {
    expressCheckoutBraintree({ makePayment, total });
  } else {
    handleError(NO_EXPRESS_BUY);
  }
};

addonExpressBuyService.initiatePayment = async (makePayment) => {
  addonExpressBuyService.instrumentToUse = find(
    addonExpressBuyService.paymentInfo.paymentInstruments,
    {
      default: 'yes',
    },
  );
  const total = getAddonTotal(addonExpressBuyService.addons);
  definePaymentProcessor({ makePayment, total });
};

addonExpressBuyService.makePayment = async (paymentFunction) => {
  addonExpressBuyService.setProgressMessage(PROGRESS_MESSAGE_PAY);

  try {
    const order = await paymentFunction();
    if (!order.data.productOrder) {
      handleError(GENERIC_ERROR);

      return;
    }
    order.data.quantity = addonExpressBuyService.quantity;
    order.data.walletAmountUsed = 0;
    order.data.cardType =
      get(
        addonExpressBuyService,
        'deal.paymentOptions.paymentInstruments[0].details.brand',
      ) ?? '';
    if (addonExpressBuyService.isBraintree) {
      order.data.cardType = PAYMENT_METHODS.paypal;
    }

    if (order?.config?.data) {
      order.data.orderValue = JSON.parse(order.config.data).cashAmount;
    }
    addonExpressBuyService.setProgressMessage(PROGRESS_MESSAGE_SUCCESS);
    order.data.currency = currencyCode;

    trackPurchase(
      addonExpressBuyService.addons,
      order,
      true,
      addonExpressBuyService.addons[0].addonType,
      true,
    );
    // success, false - not payment modal
    addonExpressBuyService.success(false);
  } catch (error) {
    if (LOG_ERRORS) console.error(error);
    addonExpressBuyService.setProgressMessage(PROGRESS_MESSAGE_FAILED);
    handleError(GENERIC_ERROR);
  }
};

addonExpressBuyService.initExpressBuy = (addons) => {
  addonExpressBuyService.addons = addons;
  addonExpressBuyService.quantity = addons.length;
  fetchPaymentOptions();
};

export default addonExpressBuyService;
